/**
 * Consumer Terms of Use — DRAFT prepared for lawyer review (see docs/legal/README.md for the
 * legal-requirements research these are built on, and the open placeholders the owner/lawyer
 * must complete). The `version` string must match server/src/legal/legalVersions.ts — bump BOTH
 * on any material change, and re-run the acceptance gate.
 */
export const CONSUMER_TERMS = {
  key: 'consumer_terms',
  version: '2026-07-09',
  title: 'Terms of Use — Individuals & Families',
  effectiveDate: '9 July 2026',
  draft: true,
  body: `
## 1. Who we are

AgedCareWise is guided financial-modelling software for Australian aged-care and retirement living
decisions, operated by **[ENTITY NAME + ABN — to be completed]** ("AgedCareWise", "we", "us").

**Licensing.** AgedCareWise provides personal financial advice tools under Australian financial
services licensing arrangements. **[LICENSING STATEMENT — to be completed on grant of the AFSL /
finalisation of licensing arrangements. The service must not be represented as licensed before
this is in place.]** Details of our advice service, including our Financial Services Guide, are
provided to you separately.

## 2. The service

- You enter information about your situation; AgedCareWise models Australian aged-care fees,
  Age Pension, cashflow and estate outcomes, compares strategies, and can produce a Statement of
  Advice.
- The modelling reflects the Australian rules current at the time you use it. Rules and rates
  change; results are projections based on stated assumptions, not guarantees of future outcomes.
- The quality of the output depends on the accuracy and completeness of what you enter. Please
  check your entries carefully.
- Some situations are too complex for guided modelling. We may tell you that part of your
  situation is outside what AgedCareWise can appropriately model and suggest you seek personal
  advice from a licensed adviser for that part.

## 3. Your account

- Keep your login details secure. You can add two-factor authentication in your account settings,
  and we recommend it.
- Your information saves automatically and is protected as described in our Privacy Policy.
- One account is for one person or couple's planning. Don't share your account.

## 4. Price and access

- The price is **$480 (including GST)**, paid once, for **4 months of full access** from the day
  you pay. There is no subscription and no automatic renewal; we do not keep your card on file.
- When your access period ends, your account locks but your information is **retained, not
  deleted** — if you return and pay again, you continue exactly where you left off.
- Payments are processed by Stripe. Your card details never touch our servers.

## 5. Refunds

Australian Consumer Law consumer guarantees apply to our service and nothing in these terms
excludes, restricts or modifies them. If our service fails to meet a guarantee, you are entitled
to the remedies the law provides, which may include a refund.
**[CHANGE-OF-MIND REFUND POLICY — owner decision required; recommend a short cooling-off window
given the upfront 4-month payment.]**

## 6. Fair use

You agree not to: use the service for anyone else's benefit on a commercial basis (professionals
have their own partner terms); attempt to access other people's information; probe, disrupt or
reverse-engineer the service; or use automated tools to bulk-extract content. The AWise assistant
has a fair-use daily allowance.

## 7. Intellectual property

The software, modelling engine, content and branding are ours (or our licensors'). Your
information is yours. The documents produced for you (including your Statement of Advice) are
yours to keep and share for your personal purposes.

## 8. Privacy

Our collection and handling of your personal information is described in the AgedCareWise
Privacy Policy, which forms part of these terms. Advice records are retained for at least
7 years as required by financial-services law, even if you close your account.

## 9. Our liability

Nothing in these terms excludes, restricts or modifies any consumer guarantee, right or remedy
under the Australian Consumer Law or the ASIC Act that cannot lawfully be excluded. Subject to
that, and to the extent permitted by law, we are not liable for loss arising from inaccurate
information you provide, from decisions you make against the guidance shown, or from events
outside our reasonable control. **[LIABILITY CLAUSE — lawyer to settle; consumer-service caps
are tightly constrained by the consumer guarantees.]**

## 10. Changes to these terms

If we make a material change to these terms, we will give you at least **30 days' notice by
email** and ask you to accept the updated terms when you next sign in. If a change materially
disadvantages you during a period you have already paid for, you may end your access and we will
refund the unused portion on a pro-rata basis. Minor changes (clarifications, typo fixes) may be
made with notice on this page.

## 11. Suspension and termination

We may suspend or close accounts used in breach of these terms (with notice and a chance to
respond, except where the breach is severe). Your rights to your information — export, deletion
subject to legal retention — survive account closure.

## 12. Complaints and disputes

Contact us first at **[SUPPORT EMAIL — to be completed]** and we will respond within a reasonable
time. **[AFCA MEMBERSHIP DETAILS — required once the AFSL is in place.]** These terms are governed
by the laws of **[STATE — to be completed]**, Australia.
`,
};

export default CONSUMER_TERMS;
